import React, { useState, useEffect, useRef, useCallback } from "react";
import "./HomeSection.css";
import logo from "../image/logo.png";

// Константы для видео
const CLOUD_NAME = "dqlxoijyx";
const VIDEO_ID = "bts";

// URL для видео
const videoUrl = `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/${VIDEO_ID}`;

// Проверка iOS
const checkIfIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const ROOT_MARGINS = {
  MOBILE: "20px",
  TABLET: "30px",
  DESKTOP: "50px"
};

const BREAKPOINTS = {
  MOBILE: 390,
  TABLET: 768
};

const HomeSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const sectionRef = useRef(null);
  const observerRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const videoRef = useRef(null);
  const isIOS = useRef(checkIfIOS());

  const getRootMargin = useCallback(() => {
    const width = window.innerWidth;
    if (width <= BREAKPOINTS.MOBILE) {
      return ROOT_MARGINS.MOBILE;
    } else if (width <= BREAKPOINTS.TABLET) {
      return ROOT_MARGINS.TABLET;
    }
    return ROOT_MARGINS.DESKTOP;
  }, []);

  const handleIntersection = useCallback((entries) => {
    entries.forEach((entry) => {
      setIsVisible(entry.isIntersecting);
    });
  }, []);

  const createOrUpdateObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const newObserver = new IntersectionObserver(handleIntersection, {
      threshold: 0.2,
      rootMargin: getRootMargin()
    });

    observerRef.current = newObserver;

    if (sectionRef.current) {
      newObserver.observe(sectionRef.current);
    }
  }, [handleIntersection, getRootMargin]);

  const handleResize = useCallback(() => {
    if (resizeTimerRef.current) {
      clearTimeout(resizeTimerRef.current);
    }

    resizeTimerRef.current = setTimeout(() => {
      createOrUpdateObserver();
    }, 150);
  }, [createOrUpdateObserver]);

  useEffect(() => {
    createOrUpdateObserver();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [createOrUpdateObserver, handleResize]);

  const handleButtonClick = useCallback(() => {
    window.location.href = "#/projects";
  }, []);

  const handleButtonKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      window.location.href = "#/projects";
    }
  }, []);

  // Обработчики видео
  const handleVideoError = useCallback((e) => {
    console.error('Video error:', e);
    setVideoError(true);
  }, []);

  const handleVideoLoad = useCallback(() => {
    console.log('Video loaded successfully');
    setVideoError(false);
  }, []);

  const titleLines = [
    { text: "COMPREHENSIVE MARKETING", delay: 0 },
    { text: "& VIDEOPRODUCTION SERVICES", delay: 0.2 }
  ];

  return (
    <section className="home-section" ref={sectionRef}>
      {/* ВИДЕОФОН */}
      <div className="home-video-background">
        <video 
          ref={videoRef}
          key={videoUrl} // Добавляем key для принудительного обновления
          autoPlay 
          muted 
          loop 
          playsInline
          className="home-video"
          onError={handleVideoError}
          onLoadedData={handleVideoLoad}
          onLoadedMetadata={handleVideoLoad}
          disablePictureInPicture
          disableRemotePlayback
        >
          <source src={videoUrl} type="video/mp4" />
          Ваш браузер не поддерживает видео тег.
        </video>
        
        {/* Затемнение */}
        <div className="home-section-overlay"></div>
      </div>
      
      {/* КОНТЕНТ */}
      <div className={`home-section-content ${isIOS.current ? 'ios-device' : ''}`}>
        <div className={`home-logo ${isVisible ? 'animate-in' : 'animate-out'}`}>
          <img 
            src={logo} 
            alt="Home Logo" 
            loading="lazy"
            width="150"
            height="50"
          />
        </div>
        
        <div className="home-text">
          <h1 className="home-title">
            {titleLines.map((line, index) => (
              <div 
                key={index}
                className={`title-line ${isVisible ? 'animate-in' : 'animate-out'}`}
                style={{ 
                  animationDelay: isVisible ? `${line.delay}s` : '0s' 
                }}
              >
                <span>{line.text}</span>
              </div>
            ))}
          </h1>
        
          <div className="home-button-container">
            <div 
              className={`read-more-button ${isVisible ? 'animate-in' : 'animate-out'}`}
              onClick={handleButtonClick}
              onKeyDown={handleButtonKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Read more about real estate projects"
            >
              <span className="button-text">VIEW PROJECT</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(HomeSection);